/**
 * Import Step 1: File Upload Component
 * 
 * First step of the 3-step import process
 * Handles file selection and validation
 */

import React, { useState, useMemo } from 'react';
import { Upload, FileText, X, ChevronDown, Search } from 'lucide-react';
import type { Account } from '../../types/account.types';
import { AccountType } from '../../types/account.types';
import clsx from 'clsx';

// Helper function to get user-friendly account type label (reserved for future use)
 
export function getAccountTypeLabel(type: AccountType): string {
  const typeLabels: Record<AccountType, string> = {
    [AccountType.BANK_CASH]: 'Everyday',
    [AccountType.SAVINGS]: 'Savings',
    [AccountType.CREDIT_CARD]: 'Liability',
    [AccountType.LOAN]: 'Liability',
    [AccountType.INVESTMENTS_SUPERANNUATION]: 'Investment',
    [AccountType.ASSETS]: 'Asset'
  };
  return typeLabels[type] || 'Account';
}

interface ImportStep1FileUploadProps {
  onFileSelected: (file: File | null) => void;
  selectedFile: File | null;
  accounts: Account[];
  selectedAccountId: string;
  onAccountChange: (accountId: string) => void;
  accountDisabled?: boolean; // If true, account selection is disabled (preselected from accounts page)
  loadingAccounts?: boolean; // If true, accounts are being loaded
  onNext: () => void;
  onCancel: () => void;
}

export function ImportStep1FileUpload({
  onFileSelected,
  selectedFile,
  accounts,
  selectedAccountId,
  onAccountChange,
  accountDisabled = false,
  loadingAccounts = false,
  onNext,
  onCancel
}: ImportStep1FileUploadProps): React.ReactElement {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    setError(null);

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // Validate file format
    const validExtensions = ['.csv', '.txt'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension && file.type !== 'text/csv' && file.type !== 'text/plain') {
      setError('File must be a CSV file');
      return;
    }

    onFileSelected(file);
  };

  const removeFile = () => {
    onFileSelected(null as any);
    setError(null);
  };

  // Group accounts by parent account type
  const getParentAccountType = (accountType: AccountType): string => {
    switch (accountType) {
      case AccountType.BANK_CASH:
        return 'Everyday';
      case AccountType.CREDIT_CARD:
      case AccountType.LOAN:
        return 'Liabilities';
      case AccountType.SAVINGS:
        return 'Savings';
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return 'Investments';
      default:
        return 'Other';
    }
  };

  // Group accounts by parent type
  const accountsByParentType = useMemo(() => {
    const filteredAccounts = accounts.filter((account) => account.type !== AccountType.ASSETS);
    const grouped = new Map<string, Account[]>();
    
    filteredAccounts.forEach(account => {
      const parentType = getParentAccountType(account.type);
      if (!grouped.has(parentType)) {
        grouped.set(parentType, []);
      }
      grouped.get(parentType)!.push(account);
    });

    // Sort accounts within each group by name
    grouped.forEach((accountList) => {
      accountList.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Sort groups in a specific order
    const groupOrder = ['Everyday', 'Liabilities', 'Savings', 'Investments', 'Other'];
    const sortedGroups = new Map<string, Account[]>();
    groupOrder.forEach(type => {
      if (grouped.has(type)) {
        sortedGroups.set(type, grouped.get(type)!);
      }
    });

    return sortedGroups;
  }, [accounts]);

  // Filter accounts based on search query
  const filteredAccountsByParentType = useMemo(() => {
    if (!accountSearchQuery.trim()) {
      return accountsByParentType;
    }

    const query = accountSearchQuery.toLowerCase();
    const filtered = new Map<string, Account[]>();

    accountsByParentType.forEach((accountList, parentType) => {
      const matchingAccounts = accountList.filter(account =>
        account.name.toLowerCase().includes(query) ||
        account.provider.toLowerCase().includes(query) ||
        parentType.toLowerCase().includes(query)
      );
      
      if (matchingAccounts.length > 0) {
        filtered.set(parentType, matchingAccounts);
      }
    });

    return filtered;
  }, [accountsByParentType, accountSearchQuery]);

  // Get selected account name
  const selectedAccountName = useMemo(() => {
    if (!selectedAccountId) return '-- Select Account --';
    const account = accounts.find(acc => acc.id === selectedAccountId);
    if (!account) return '-- Select Account --';
    return account.name;
  }, [selectedAccountId, accounts]);

  const handleAccountSelect = (accountId: string) => {
    onAccountChange(accountId);
    setIsAccountDropdownOpen(false);
    setAccountSearchQuery('');
  };

  return (
    <div className="space-y-6">
      {/* Account Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Account <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => !accountDisabled && !loadingAccounts && setIsAccountDropdownOpen(!isAccountDropdownOpen)}
            disabled={accountDisabled || loadingAccounts}
            className={clsx(
              'flex items-center justify-between w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-left',
              accountDisabled || loadingAccounts ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : 'hover:border-gray-400 dark:hover:border-gray-500'
            )}
            aria-label="Select account"
            aria-expanded={isAccountDropdownOpen}
            aria-required="true"
          >
            <span className={clsx(
              'text-sm truncate',
              !selectedAccountId && 'text-gray-500 dark:text-gray-400'
            )}>
              {loadingAccounts ? 'Loading accounts...' : selectedAccountName}
            </span>
            <ChevronDown
              className={clsx(
                'h-4 w-4 ml-2 flex-shrink-0 transition-transform text-gray-400',
                isAccountDropdownOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>

          {isAccountDropdownOpen && !accountDisabled && !loadingAccounts && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => {
                  setIsAccountDropdownOpen(false);
                  setAccountSearchQuery('');
                }}
                aria-hidden="true"
              />
              <div className="absolute left-0 mt-2 w-full z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
                {/* Search Box */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={accountSearchQuery}
                      onChange={(e) => setAccountSearchQuery(e.target.value)}
                      placeholder="Search accounts..."
                      className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      autoFocus
                    />
                    {accountSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setAccountSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Account List */}
                <div className="max-h-96 overflow-y-auto">
                  {filteredAccountsByParentType.size === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No accounts found
                    </div>
                  ) : (
                    Array.from(filteredAccountsByParentType.entries()).map(([parentType, accountList]) => (
                      <div key={parentType} className="border-t border-gray-100 dark:border-gray-700 first:border-t-0">
                        {/* Parent Type Header */}
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          {parentType}
                        </div>

                        {/* Accounts in this type */}
                        {accountList.map((account) => {
                          const isSelected = selectedAccountId === account.id;
                          const last4 = account.accountNumber && account.accountNumber.length >= 4
                            ? account.accountNumber.slice(-4)
                            : '';
                          
                          return (
                            <button
                              key={account.id}
                              type="button"
                              onClick={() => handleAccountSelect(account.id)}
                              className={clsx(
                                'w-full px-4 py-2.5 text-sm transition-colors text-left',
                                isSelected
                                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              )}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{account.name}</div>
                                {account.provider && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {account.provider}{last4 ? ` • ${last4}` : ''}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {!loadingAccounts && accounts.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            No accounts found. Please create an account first.
          </p>
        )}
      </div>

      {/* File Upload Area - Only show when no file is selected */}
      {!selectedFile && (
        <div
          className={`border-2 border-dashed rounded-xl px-6 py-8 md:px-8 md:py-10 transition-colors ${
            dragActive
              ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col gap-4 text-center md:flex-row md:items-center md:gap-6 md:text-left">
            <div className="w-16 h-16 mx-auto md:mx-0 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <Upload className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Drop file here or click to upload
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Support CSV format (max 10MB)
              </p>
            </div>
            <div className="flex justify-center md:justify-end">
              <label className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap">
                Choose File
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileInput}
                  className="hidden"
                  aria-label="Select CSV file"
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4" role="alert">
          <div className="flex items-center">
            <X className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Selected File Display */}
      {selectedFile && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <button
              onClick={removeFile}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              aria-label="Remove file"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Privacy Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-1">Privacy & Security</h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              CSV files are parsed locally on your device. Transaction data is securely stored in your account when you import.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
        >
          Cancel
        </button>
        <button
          onClick={onNext}
          disabled={!selectedFile || !selectedAccountId}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
        >
          Next: Map Columns
        </button>
      </div>
    </div>
  );
}

